const MUSIC_TAB_KEY = 'tst:active-music-tab:v1';
const STALE_MS = 5000;
const HEARTBEAT_MS = 1500;

const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
const listeners = new Set<() => void>();
let heartbeatId: number | null = null;

interface MusicTabState {
  id: string;
  updatedAt: number;
}

export function claimMusicTab(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return true;
  if (!canOwnMusic()) return false;
  try {
    window.localStorage.setItem(MUSIC_TAB_KEY, JSON.stringify({ id: TAB_ID, updatedAt: Date.now() }));
    ensureHeartbeat();
    return true;
  } catch {
    return true;
  }
}

export function isActiveMusicTab(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return true;
  if (!canOwnMusic()) return false;
  const state = readState();
  if (!state || Date.now() - state.updatedAt > STALE_MS) return claimMusicTab();
  return state.id === TAB_ID;
}

export function subscribeMusicTabLock(listener: () => void): () => void {
  listeners.add(listener);
  setupEventListeners();
  return () => listeners.delete(listener);
}

function readState(): MusicTabState | null {
  try {
    const raw = window.localStorage.getItem(MUSIC_TAB_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MusicTabState>;
    if (typeof parsed.id !== 'string' || typeof parsed.updatedAt !== 'number') return null;
    return { id: parsed.id, updatedAt: parsed.updatedAt };
  } catch {
    return null;
  }
}

function canOwnMusic(): boolean {
  // Only "tab is backgrounded" releases music ownership. Window blur
  // (clicking on devtools, another app window, OS chrome) used to count
  // here too, but that punished players who were just using their
  // computer — they'd lose ambient music every time they clicked off the
  // game. Cross-tab coordination still works because each tab listens
  // for the `storage` event below.
  return document.visibilityState !== 'hidden';
}

function ensureHeartbeat(): void {
  if (heartbeatId !== null || typeof window === 'undefined') return;
  heartbeatId = window.setInterval(() => {
    if (isActiveMusicTab()) {
      try {
        window.localStorage.setItem(MUSIC_TAB_KEY, JSON.stringify({ id: TAB_ID, updatedAt: Date.now() }));
      } catch {
        /* ignore */
      }
    }
  }, HEARTBEAT_MS);
}

function notify(): void {
  for (const listener of listeners) listener();
}

let didSetup = false;
function setupEventListeners(): void {
  if (didSetup || typeof window === 'undefined' || typeof document === 'undefined') return;
  didSetup = true;
  window.addEventListener('storage', (event) => {
    if (event.key === MUSIC_TAB_KEY) notify();
  });
  // `focus` re-claims when the user returns from another tab/window so the
  // music resumes promptly after a real tab switch (not used as a release
  // trigger — that's `visibilitychange`'s job).
  window.addEventListener('focus', () => {
    claimMusicTab();
    notify();
  });
  window.addEventListener('pointerdown', () => {
    claimMusicTab();
    notify();
  }, { capture: true });
  window.addEventListener('keydown', () => {
    claimMusicTab();
    notify();
  }, { capture: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') claimMusicTab();
    notify();
  });
}
